<template>
  <a-row :gutter="20" :class="bem.e('table-row')">
    <a-col style="display: flex" :span="7" :class="bem.e('table-col')">
      <p :style="{ width: 10 * props.layer + 'px' }" />
      <div style="margin-right: 30px" :class="bem.m('table-row-icon')">
        <ll-button
          v-if="innerData.type === 'object' || innerData.type === 'array'"
          type="link"
          style="width: 10px"
          :disabled="disabled"
          @click="expanded = !expanded"
        >
          <ll-icon
            v-if="!expanded"
            style="font-size: 14px"
            icon-name="icon-list-expand"
          />
          <ll-icon
            v-else
            style="font-size: 14px"
            icon-name="icon-list-retract"
          />
        </ll-button>
      </div>

      <div v-if="!props.parentData" :class="bem.m('table-row-tag')">根节点</div>
      <template v-else>
        <div
          v-if="props.parentData.type === 'array'"
          :class="bem.m('table-row-tag')"
        >
          ITEMS
        </div>
        <ll-input
          v-if="props.parentData.type === 'object'"
          v-model:value="innerData.name"
          :disabled="disabled"
          placeholder="添加字段名"
          style="width: 100%"
          :bordered="false"
        ></ll-input>
      </template>
    </a-col>
    <a-col :span="3" :class="bem.e('table-col')">
      <ll-select
        v-model:value="innerData.type"
        :options="jsonXmlTypeOptions"
        :disabled="disabled"
        style="width: 100%"
        @change="handleChange"
      />
    </a-col>
    <a-col :span="4" :class="bem.e('table-col')">
      <click-input
        v-model:value="innerData.mock"
        placeholder="Mock"
        :disabled="innerData.type === 'object' || disabled"
        style="width: 100%"
      />
    </a-col>
    <a-col :span="4" :class="bem.e('table-col')">
      <ll-input
        v-model:value="innerData.chineseName"
        :disabled="disabled"
        placeholder="中文名"
        style="width: 100%"
      />
    </a-col>
    <a-col :span="4" :class="bem.e('table-col')">
      <click-input
        v-model:value="innerData.description"
        :disabled="disabled"
        placeholder="说明"
        style="width: 100%"
      />
    </a-col>
    <a-col :span="2">
      <div :class="bem.e('table-operation')">
        <ll-tooltip
          v-if="
            (props.parentData &&
              props.parentData.type === 'object' &&
              innerData.type !== 'object') ||
            (!props.parentData && innerData.type === 'object')
          "
          :tip="
            getTip(parentData, innerData) === 'child'
              ? '添加子节点'
              : '添加相邻节点'
          "
        >
          <ll-button
            type="link"
            :disabled="disabled"
            :class="bem.m('table-icon')"
            @click="
              handleAdd(
                getTip(parentData, innerData),
                props.parentData,
                innerData
              )
            "
          >
            <ll-icon icon-name="icon-list-add" />
          </ll-button>
        </ll-tooltip>

        <ll-dropdown
          :dropdown-items="customOverlay(props.parentData, innerData)"
          placement="bottomRight"
          @claim-selected="handleClaimSelected"
        >
          <ll-button
            v-if="
              props.parentData &&
              props.parentData.type === 'object' &&
              innerData.type === 'object'
            "
            :class="bem.m('table-icon')"
            :disabled="disabled"
            type="link"
          >
            <ll-icon icon-name="icon-list-add" />
          </ll-button>
        </ll-dropdown>

        <a-popconfirm
          v-if="props.parentData && props.parentData.type !== 'array'"
          title="确定要移除吗？"
          ok-text="确认"
          cancel-text="取消"
          placement="topRight"
          :overlay-class-name="bem.e('popconfirm')"
          :disabled="disabled"
          @confirm="handleConfirm(props.parentData, innerData)"
        >
          <ll-button
            type="link"
            :disabled="disabled"
            :class="bem.m('table-icon')"
          >
            <ll-icon icon-name="icon-remove" />
          </ll-button>
        </a-popconfirm>
      </div>
    </a-col>
  </a-row>

  <template v-if="expanded">
    <a-row
      v-if="
        innerData.type === 'object' &&
        innerData.children &&
        innerData.children.length === 0
      "
      :gutter="20"
      :class="bem.e('table-row')"
    >
      <a-col style="display: flex" :span="7" :class="bem.e('table-col')">
        <p :style="{ width: 10 * props.layer + 'px' }" />
        <div style="margin-right: 30px" :class="bem.m('table-row-icon')" />
        <ll-input
          v-model:value="addValue"
          placeholder="添加字段名"
          :bordered="false"
          :disabled="disabled"
          @input="handleInput"
        />
      </a-col>
    </a-row>

    <template v-if="innerData.children && innerData.children.length > 0">
      <template v-for="(child, index) in innerData.children" :key="index">
        <table-row
          :parent-data="innerData"
          :list="innerData.children"
          :data="child"
          :layer="props.layer + 1"
          :tree-key="props.treeKey + '-' + (index + 1)"
          @change="handleRowChange"
        />
      </template>
    </template>
  </template>
</template>

<script setup lang="ts">
import { ref, watch, inject } from 'vue'

import { createNamespace } from '@ll-plus/utils'

import {
  jsonXmlTypeOptions,
  jsonXmlTableRowProps,
  jsonXmlTableRowEmits
} from '../config'

import TableRow from './json-xml-table-row.vue'

import ClickInput from './click-input.vue'

defineOptions({ name: 'LlJsonXmlTableRow' })

const bem = createNamespace('json-xml')

const props = defineProps(jsonXmlTableRowProps)

const emits = defineEmits(jsonXmlTableRowEmits)

const disabled = inject('disabled') as boolean

const innerData = ref(props.data)

const parentData = ref(props.parentData)

const expanded = ref(true)

const addValue = ref('')

const customOverlay = (parentData, innerData) => [
  {
    label: '添加子节点',
    type: 'child',
    parentData,
    innerData
  },
  {
    label: '添加相邻节点',
    type: 'next',
    parentData,
    innerData
  }
]

const getTip = (parentData, innerData) => {
  if (
    !parentData ||
    (parentData.type === 'array' && innerData.type === 'object')
  ) {
    return 'child'
  }
  if (parentData.type === 'object' && innerData.type !== 'object') {
    return 'next'
  }
}

const handleChange = () => {
  if (innerData.value.type === 'object' || innerData.value.type === 'array') {
    innerData.value.children = []
    if (innerData.value.type === 'array') {
      innerData.value.children[0] = {
        key: props.treeKey + '-1',
        name: 'ITEMS',
        type: 'string',
        value: '',
        chineseName: '',
        description: '',
        mock: ''
      }
    }
  } else {
    delete innerData.value.children
  }
}

const handleConfirm = (parentData, innerData) => {
  const findIndex = parentData.children.findIndex(
    item => item.key === innerData.key
  )
  parentData.children.splice(findIndex, 1)
}

const handleInput = () => {
  innerData.value.children = []
  innerData.value.children[0] = {
    key: props.treeKey + '-1',
    name: addValue.value,
    type: 'string',
    value: '',
    chineseName: '',
    description: '',
    mock: ''
  }
  addValue.value = ''
}

const handleAdd = (type, parentData, innerData) => {
  if (type === 'next') {
    parentData.children.push({
      key: `${parentData.children.length + 1}`,
      name: '',
      type: 'string',
      value: '',
      chineseName: '',
      description: '',
      mock: ''
    })
  } else {
    innerData.children.push({
      key: props.treeKey + `${innerData.children.length + 1}`,
      name: '',
      type: 'string',
      value: '',
      chineseName: '',
      description: '',
      mock: ''
    })
  }
}
const handleClaimSelected = item => {
  const { type, parentData, innerData } = item
  handleAdd(type, parentData, innerData)
}

const handleRowChange = child => {
  if (innerData.value.children) {
    const index = innerData.value.children.findIndex(
      item => item.key === child.key
    )
    innerData.value.children[index] = child
  }
}
watch(
  innerData,
  () => {
    emits('change', innerData.value)
  },
  {
    deep: true
  }
)
</script>
